import { CompetitorRow, EntitySpend, MoverStats } from './types';

// Client-side analysis over the /competitors payload: $-exposure weighting and
// per-campaign grouping. Mirrors the server-side calibration (movers.ts in the
// collector): exposure = Σ over ad groups where the domain gained share of
// (share gain × our weekly spend on that ad group).

const BELOW_10_IMPUTED = 0.05;

// Google Ads URL `ocid` is the internal obfuscated customer id, not the
// customer number — must mirror GOOGLE_ADS_OCID in the collector's
// intelligence/config.ts. Fallback keeps unmapped accounts functional-ish.
const GOOGLE_ADS_OCID: Record<string, string> = {
  '6907204135': '313163905', // Dosaze (all geos)
};

export function ocidFor(accountId: string): string {
  return GOOGLE_ADS_OCID[accountId] ?? accountId;
}

const shareOf = (r: CompetitorRow): number | null =>
  r.impression_share_below_10 ? BELOW_10_IMPUTED : r.impression_share;

const average = (values: number[]): number | null =>
  values.length ? values.reduce((s, v) => s + v, 0) / values.length : null;

function isCompleteWeek(weekStart: string, asOfDate = new Date().toISOString().slice(0, 10)): boolean {
  const start = new Date(`${weekStart}T00:00:00Z`);
  const asOf = new Date(`${asOfDate}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(asOf.getTime())) return true;
  start.setUTCDate(start.getUTCDate() + 7);
  return start.getTime() <= asOf.getTime();
}

function windows(
  rows: CompetitorRow[],
  asOfDate = new Date().toISOString().slice(0, 10),
  currentWeeks = 2,
  priorWeeks = 4,
): { current: Set<string>; prior: Set<string> } {
  const weeks = [...new Set(rows.map(r => String(r.week_start).slice(0, 10)))]
    .filter(w => isCompleteWeek(w, asOfDate))
    .sort()
    .reverse();
  return {
    current: new Set(weeks.slice(0, currentWeeks)),
    prior: new Set(weeks.slice(currentWeeks, currentWeeks + priorWeeks)),
  };
}

export interface EntityDelta {
  adGroupId: string | null;
  campaignId: string | null;
  entityLabel: string;
  currentShare: number | null;
  priorShare: number | null;
  deltaPp: number | null;
  weeklySpend: number;
  // Our spend here ramped from ~nothing — the competitor's "gain" on this ad
  // group is us entering their auctions, not them attacking.
  ourSpendRamped: boolean;
  history: string;
}

export function spendMap(entitySpend: EntitySpend[] | undefined): Map<string, number> {
  return new Map((entitySpend ?? []).map(e => [e.adGroupId, e.weeklySpend]));
}

// Mirrors the collector's calibrated classification (movers.ts): a mover whose
// exposure sits mostly on ad groups where OUR spend ramped from ~zero is "our
// expansion" (e.g. rest.com after the ThermaCool Comforter launch), not an
// attack (thepillowhome, whose gains sat on flat-spend ad groups).
const RAMP_RATIO = 0.25;

export type MoverClassification = 'competitor_gain' | 'mixed' | 'our_expansion';

export function classifyExpansion(ourExpansionShare: number): MoverClassification {
  if (ourExpansionShare >= 0.6) return 'our_expansion';
  if (ourExpansionShare >= 0.3) return 'mixed';
  return 'competitor_gain';
}

// The mover-category filter, shared by the Movers and By-campaign tabs so the
// two stay in lockstep. New-entrant is orthogonal to the expansion
// classification, so it's a selectable category of its own.
export type FilterCategory = MoverClassification | 'new_entrant';

export const FILTER_OPTIONS: { value: FilterCategory; label: string }[] = [
  { value: 'competitor_gain', label: 'Competitor gains' },
  { value: 'our_expansion', label: 'Our expansion' },
  { value: 'mixed', label: 'Partly our expansion' },
  { value: 'new_entrant', label: 'New entrants' },
];

// Multi-select semantics: no categories picked = everything; otherwise an item
// matches if it falls in ANY selected category (union, since new-entrant can
// co-occur with any classification).
export function matchesCategories(
  item: { isNewEntrant: boolean; classification: MoverClassification },
  selected: FilterCategory[],
): boolean {
  if (selected.length === 0) return true;
  return (selected.includes('new_entrant') && item.isNewEntrant) || selected.includes(item.classification);
}

// Deep link to the Google Ads Auction insights page, scoped to a campaign
// (and optionally ad group), with the dashboard's analysis window as the date
// range. The `period=YYYYMMDD-YYYYMMDD` param is best-effort (undocumented);
// if Google ignores it the link still lands on the right entity's auction
// insights.
export function auctionInsightsUrl(
  accountId: string,
  campaignId: string | null,
  { adGroupId, start, end }: { adGroupId?: string | null; start?: string; end?: string } = {},
): string {
  const params = [`ocid=${ocidFor(accountId)}`];
  if (campaignId) params.push(`campaignId=${campaignId}`);
  if (adGroupId) params.push(`adGroupId=${adGroupId}`);
  if (start && end) params.push(`period=${start.replace(/-/g, '')}-${end.replace(/-/g, '')}`);
  return `https://ads.google.com/aw/insights/auctioninsights?${params.join('&')}`;
}

// The 6 completed weeks (2 current + 4 prior) the movers math compares — so
// deep links open Google Ads on the same window the dashboard is showing.
export function analysisPeriod(
  rows: CompetitorRow[],
  asOfDate?: string,
): { start: string; end: string } | null {
  const { current, prior } = windows(rows, asOfDate);
  const weeks = [...current, ...prior].sort();
  if (weeks.length === 0) return null;
  const last = new Date(`${weeks[weeks.length - 1]}T00:00:00Z`);
  last.setUTCDate(last.getUTCDate() + 6);
  return { start: weeks[0], end: last.toISOString().slice(0, 10) };
}

export function priorSpendMap(entitySpend: EntitySpend[] | undefined): Map<string, number> {
  return new Map(
    (entitySpend ?? [])
      .filter(e => e.priorWeeklySpend != null)
      .map(e => [e.adGroupId, e.priorWeeklySpend as number]),
  );
}

function isRamped(
  entityKey: string | null,
  weeklySpend: number,
  priorSpend: Map<string, number> | undefined,
): boolean {
  if (!priorSpend || priorSpend.size === 0 || entityKey == null || weeklySpend <= 0) return false;
  return (priorSpend.get(entityKey) ?? 0) < weeklySpend * RAMP_RATIO;
}

// Fraction of a domain's $-exposure sitting on our newly ramped ad groups.
export function expansionShareForDomain(deltas: EntityDelta[]): number {
  let exposure = 0;
  let ramped = 0;
  for (const d of deltas) {
    if ((d.deltaPp ?? 0) <= 0 || d.weeklySpend <= 0) continue;
    const contribution = ((d.deltaPp ?? 0) / 100) * d.weeklySpend;
    exposure += contribution;
    if (d.ourSpendRamped) ramped += contribution;
  }
  return exposure > 0 ? ramped / exposure : 0;
}

// The slice of a domain's exposure on ad groups where our spend did NOT ramp —
// the genuine-attack dollars. Mirrors MoverAlert.attackExposureWeekly in the
// collector: a domain can be "our expansion" overall yet still attack a mature
// flat-spend ad group, and this isolates that so the grid can flag it.
export function attackExposureForDomain(deltas: EntityDelta[]): number {
  return Math.round(
    deltas
      .filter(d => (d.deltaPp ?? 0) > 0 && d.weeklySpend > 0 && !d.ourSpendRamped)
      .reduce((s, d) => s + ((d.deltaPp ?? 0) / 100) * d.weeklySpend, 0),
  );
}

// Attack exposure at/above this pages in the digest; the grid flags it too so
// the two surfaces agree. Matches ATTACK_EXPOSURE_FLOOR in the collector.
export const ATTACK_EXPOSURE_FLOOR = 500;

// Per-entity deltas for one domain (drill-down + exposure input).
export function entityDeltas(
  rows: CompetitorRow[],
  domain: string,
  spend: Map<string, number>,
  priorSpend?: Map<string, number>,
): EntityDelta[] {
  const { current, prior } = windows(rows);
  const weeksDesc = [...new Set(rows.map(r => String(r.week_start).slice(0, 10)))]
    .filter(w => isCompleteWeek(w))
    .sort()
    .reverse();
  const byEntity = new Map<string, { row: CompetitorRow; byWeek: Map<string, number> }>();
  for (const row of rows) {
    if (row.domain !== domain) continue;
    const key = String(row.ad_group_id ?? row.ad_group_name ?? row.campaign_id ?? 'account');
    const share = shareOf(row);
    if (share == null) continue;
    if (!byEntity.has(key)) byEntity.set(key, { row, byWeek: new Map() });
    byEntity.get(key)!.byWeek.set(String(row.week_start).slice(0, 10), share);
  }

  const result: EntityDelta[] = [];
  for (const [key, { row, byWeek }] of byEntity) {
    const cur = average([...current].map(w => byWeek.get(w)).filter((v): v is number => v != null));
    const pri = average([...prior].map(w => byWeek.get(w)).filter((v): v is number => v != null));
    const history = weeksDesc
      .slice(0, 6)
      .reverse()
      .map(w => (byWeek.has(w) ? `${Math.round(byWeek.get(w)! * 100)}%` : '·'))
      .join(' → ');
    result.push({
      adGroupId: row.ad_group_id != null ? String(row.ad_group_id) : null,
      campaignId: row.campaign_id != null ? String(row.campaign_id) : null,
      entityLabel: `${row.campaign_name ?? row.campaign_id ?? ''} › ${row.ad_group_name ?? row.ad_group_id ?? 'account'}`,
      currentShare: cur,
      priorShare: pri,
      deltaPp: cur != null ? Number(((cur - (pri ?? 0)) * 100).toFixed(1)) : null,
      weeklySpend: spend.get(key) ?? 0,
      ourSpendRamped: isRamped(key, spend.get(key) ?? 0, priorSpend),
      history,
    });
  }
  return result.sort(
    (a, b) => (b.deltaPp ?? 0) * b.weeklySpend - (a.deltaPp ?? 0) * a.weeklySpend,
  );
}

// Spend-weighted average delta across a domain's entities — the same headline
// number the collector's computeMoverAlerts puts in the digest, so the grid's
// Δ column and the Slack alert never disagree. Weights are OUR weekly spend
// (zero-spend entities carry no weight); with no spend anywhere, falls back to
// the unweighted mean.
export function weightedDeltaPp(deltas: EntityDelta[]): number | null {
  const observed = deltas.filter(d => d.deltaPp != null);
  if (observed.length === 0) return null;
  let weightedSum = 0;
  let weightSum = 0;
  for (const d of observed) {
    weightedSum += (d.deltaPp as number) * d.weeklySpend;
    weightSum += d.weeklySpend;
  }
  const avg = weightSum > 0
    ? weightedSum / weightSum
    : average(observed.map(d => d.deltaPp as number)) ?? 0;
  return Number(avg.toFixed(1));
}

// $-exposure per domain, for ranking the movers grid by money instead of pp.
export function exposureByDomain(
  rows: CompetitorRow[],
  movers: MoverStats[],
  spend: Map<string, number>,
): Map<string, number> {
  const result = new Map<string, number>();
  for (const mover of movers) {
    if (mover.isYou) continue;
    const exposure = entityDeltas(rows, mover.domain, spend)
      .filter(e => (e.deltaPp ?? 0) > 0)
      .reduce((s, e) => s + ((e.deltaPp ?? 0) / 100) * e.weeklySpend, 0);
    result.set(mover.domain, Math.round(exposure));
  }
  return result;
}

export interface CampaignMovers {
  campaignId: string | null;
  campaignName: string;
  weeklySpend: number;
  topGainers: Array<{
    domain: string;
    maxAdGroupDeltaPp: number;
    affectedAdGroups: number;
    affectedSpend: number;
    exposureWeekly: number;
    isNewEntrant: boolean;
    isReturning: boolean;
    ourExpansionShare: number;
    classification: MoverClassification;
  }>;
}

const MIN_ENTITIES = 2;
const MIN_MOVER_EXPOSURE = 500;
const MIN_ENTRANT_EXPOSURE = 250;

// Campaign-rows view: our spend per campaign + competitors ranked by dollars
// exposed inside affected ad groups. Auction Insights percentages cannot be
// rolled up by averaging: exact campaign share requires eligible-impression
// denominators that the export does not contain.
export function campaignMovers(
  rows: CompetitorRow[],
  entitySpend: EntitySpend[] | undefined,
  topN = 5,
  asOfDate = new Date().toISOString().slice(0, 10),
): CampaignMovers[] {
  const { current, prior } = windows(rows, asOfDate);
  const weeklySpendByEntity = spendMap(entitySpend);
  const priorByEntity = priorSpendMap(entitySpend);
  const campaignSpend = new Map<string, number>();
  for (const e of entitySpend ?? []) {
    if (e.campaignId == null) continue;
    campaignSpend.set(e.campaignId, (campaignSpend.get(e.campaignId) ?? 0) + e.weeklySpend);
  }

  // campaign → domain → entity → week → share
  const tree = new Map<string, { name: string; domains: Map<string, Map<string, Map<string, number>>> }>();
  for (const row of rows) {
    if (row.is_you || (row.level !== 'ad_group' && row.ad_group_id == null)) continue;
    const cid = String(row.campaign_id ?? row.campaign_name ?? 'unknown');
    const share = shareOf(row);
    if (share == null) continue;
    if (!tree.has(cid)) {
      tree.set(cid, { name: String(row.campaign_name ?? cid), domains: new Map() });
    }
    const campaign = tree.get(cid)!;
    const ent = String(row.ad_group_id ?? row.ad_group_name ?? 'account');
    if (!campaign.domains.has(row.domain)) campaign.domains.set(row.domain, new Map());
    const entities = campaign.domains.get(row.domain)!;
    if (!entities.has(ent)) entities.set(ent, new Map());
    entities.get(ent)!.set(String(row.week_start).slice(0, 10), share);
  }

  const result: CampaignMovers[] = [];
  for (const [cid, campaign] of tree) {
    const gainers: CampaignMovers['topGainers'] = [];
    for (const [domain, entities] of campaign.domains) {
      const positiveDeltas: Array<{ delta: number; spend: number; ramped: boolean }> = [];
      let presentPrior = false;
      const firstCurrentWeek = [...current].sort()[0];
      const presentBeforeCurrent = firstCurrentWeek != null
        && [...entities.values()].some(byWeek => [...byWeek.keys()].some(w => w < firstCurrentWeek));
      for (const [entityId, byWeek] of entities) {
        const cur = average([...current].map(w => byWeek.get(w)).filter((v): v is number => v != null));
        if ([...prior].some(w => byWeek.has(w))) presentPrior = true;
        if (cur == null) continue;
        const pri = average([...prior].map(w => byWeek.get(w)).filter((v): v is number => v != null));
        const delta = cur - (pri ?? 0);
        if (delta > 0) {
          const entitySpendNow = weeklySpendByEntity.get(entityId) ?? 0;
          positiveDeltas.push({
            delta,
            spend: entitySpendNow,
            ramped: isRamped(entityId, entitySpendNow, priorByEntity),
          });
        }
      }
      if (positiveDeltas.length === 0) continue;
      const reappeared = !presentPrior && prior.size > 0;
      const isNewEntrant = reappeared && !presentBeforeCurrent;
      const isReturning = reappeared && presentBeforeCurrent;
      const exposureWeekly = Math.round(positiveDeltas.reduce((sum, e) => sum + e.delta * e.spend, 0));
      const rampedExposure = positiveDeltas.reduce((sum, e) => sum + (e.ramped ? e.delta * e.spend : 0), 0);
      const affectedSpend = Math.round(positiveDeltas.reduce((sum, e) => sum + e.spend, 0));
      const threshold = isNewEntrant ? MIN_ENTRANT_EXPOSURE : MIN_MOVER_EXPOSURE;
      if (positiveDeltas.length < MIN_ENTITIES || exposureWeekly < threshold) continue;
      const ourExpansionShare = exposureWeekly > 0 ? rampedExposure / exposureWeekly : 0;
      gainers.push({
        domain,
        maxAdGroupDeltaPp: Number((Math.max(...positiveDeltas.map(e => e.delta)) * 100).toFixed(1)),
        affectedAdGroups: positiveDeltas.length,
        affectedSpend,
        exposureWeekly,
        isNewEntrant,
        isReturning,
        ourExpansionShare: Number(ourExpansionShare.toFixed(2)),
        classification: classifyExpansion(ourExpansionShare),
      });
    }
    gainers.sort((a, b) => b.exposureWeekly - a.exposureWeekly);
    result.push({
      campaignId: /^\d+$/.test(cid) ? cid : null,
      campaignName: campaign.name,
      weeklySpend: campaignSpend.get(cid) ?? 0,
      topGainers: gainers.slice(0, topN),
    });
  }
  return result.sort((a, b) => b.weeklySpend - a.weeklySpend);
}

// ---------- Campaign-first health (matches the campaign Slack alert) ----------

export interface CampaignCompetitor {
  domain: string;
  currentShare: number | null;
  deltaPp: number; // their share change, signed (positive = gaining on us)
  exposureWeekly: number; // their gain × our whole-campaign spend
  isNewEntrant: boolean;
  classification: MoverClassification;
}

export interface CampaignHealth {
  campaignId: string | null;
  campaignName: string;
  weeklySpend: number;
  // OUR campaign impression share, from the export's "You" rows — the same
  // source/denominator as the competitor rows, so "we lose vs they gain" is a
  // like-for-like comparison.
  ourCurrentShare: number | null;
  ourDeltaPp: number | null; // signed: negative = we lost share
  competitors: CampaignCompetitor[];
}

// Per-campaign health computed from the TRUE campaign-level rows (level =
// 'campaign'), so the dashboard shows the same campaign share the Slack alert
// fires on — not the ad-group rollup that campaignMovers() uses.
export function campaignCompetitors(
  rows: CompetitorRow[],
  entitySpend: EntitySpend[] | undefined,
  asOfDate = new Date().toISOString().slice(0, 10),
  currentWeeks = 2,
  priorWeeks = 4,
): CampaignHealth[] {
  const { current, prior } = windows(rows, asOfDate, currentWeeks, priorWeeks);
  // Whole-campaign spend (current + prior), summed from ad-group entitySpend.
  const spendByCampaign = new Map<string, number>();
  const priorSpendByCampaign = new Map<string, number>();
  for (const e of entitySpend ?? []) {
    if (e.campaignId == null) continue;
    spendByCampaign.set(e.campaignId, (spendByCampaign.get(e.campaignId) ?? 0) + e.weeklySpend);
    priorSpendByCampaign.set(e.campaignId, (priorSpendByCampaign.get(e.campaignId) ?? 0) + (e.priorWeeklySpend ?? 0));
  }

  const tree = new Map<
    string,
    { name: string; you: Map<string, number>; domains: Map<string, Map<string, number>> }
  >();
  for (const row of rows) {
    if (row.level !== 'campaign') continue; // Google-measured campaign share only
    const share = shareOf(row);
    if (share == null) continue;
    const cid = String(row.campaign_id ?? row.campaign_name ?? 'unknown');
    if (!tree.has(cid)) tree.set(cid, { name: String(row.campaign_name ?? cid), you: new Map(), domains: new Map() });
    const c = tree.get(cid)!;
    const wk = String(row.week_start).slice(0, 10);
    if (row.is_you) {
      c.you.set(wk, share);
      continue;
    }
    if (!c.domains.has(row.domain)) c.domains.set(row.domain, new Map());
    c.domains.get(row.domain)!.set(wk, share);
  }

  const avgOver = (byWeek: Map<string, number>, set: Set<string>): number | null =>
    average([...set].map(w => byWeek.get(w)).filter((v): v is number => v != null));

  const result: CampaignHealth[] = [];
  for (const [cid, c] of tree) {
    const spend = spendByCampaign.get(cid) ?? 0;
    // Whole-campaign ramp: spend went from ~zero to real → a competitor's "gain"
    // is us entering their auctions, not an attack (mirrors the collector).
    const rampedCampaign = (priorSpendByCampaign.get(cid) ?? 0) < spend * RAMP_RATIO;
    const ourCur = avgOver(c.you, current);
    const ourPri = avgOver(c.you, prior);
    const competitors: CampaignCompetitor[] = [];
    for (const [domain, byWeek] of c.domains) {
      const cur = avgOver(byWeek, current);
      if (cur == null) continue;
      const pri = avgOver(byWeek, prior);
      const presentPrior = [...prior].some(w => byWeek.has(w));
      const delta = cur - (pri ?? 0);
      competitors.push({
        domain,
        currentShare: cur,
        deltaPp: Number((delta * 100).toFixed(1)),
        exposureWeekly: delta > 0 ? Math.round(delta * spend) : 0,
        isNewEntrant: !presentPrior && prior.size > 0,
        classification: classifyExpansion(rampedCampaign && delta > 0 ? 1 : 0),
      });
    }
    competitors.sort((a, b) => b.exposureWeekly - a.exposureWeekly);
    result.push({
      campaignId: /^\d+$/.test(cid) ? cid : null,
      campaignName: c.name,
      weeklySpend: spend,
      ourCurrentShare: ourCur,
      ourDeltaPp: ourCur != null ? Number(((ourCur - (ourPri ?? ourCur)) * 100).toFixed(1)) : null,
      competitors,
    });
  }
  return result.sort((a, b) => b.weeklySpend - a.weeklySpend);
}
